import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIos from "@mui/icons-material/ArrowBackIos";
import Search from "@mui/icons-material/Search";
import { DataGrid } from "@mui/x-data-grid";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import { PHARMACY_HALO_COLORS } from "@/config/colors";
import { getAdminApiErrorMessage } from "@/utils/adminApiErrors";
import { pharmacyHaloDataGridSx } from "@/utils/adminTableStyles";

const BLUE = PHARMACY_HALO_COLORS.BLUE;

function fmtDateAU(iso) {
  if (!iso) return "—";
  const p = String(iso).split("-");
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : iso;
}

const AdminInactiveTerminalsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [nameFilter, setNameFilter] = useState("");
  const fetched = useRef(false);

  const fetchData = useCallback(async () => {
    if (fetched.current) return;
    fetched.current = true;
    try {
      setLoading(true);
      setError(null);
      const res = await phappsRequest(API_PATHS.ADMIN_TERMINAL_ANALYTICS, "GET");
      setData(res);
    } catch (e) {
      fetched.current = false;
      setError(getAdminApiErrorMessage(e, "Failed to load terminal analytics"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const allRows = (data?.InactiveSubscribers ?? []).map((r, i) => ({
    id: r.ChemistUUID || i,
    ...r,
  }));

  const filteredRows = nameFilter.trim()
    ? allRows.filter((r) =>
        String(r.chemistName || "").toLowerCase().includes(nameFilter.trim().toLowerCase())
      )
    : allRows;

  const columns = [
    {
      field: "chemistName",
      headerName: "Pharmacy",
      flex: 1,
      minWidth: 160,
      renderCell: (p) => (
        <Typography variant="body2" noWrap sx={{ width: 1, fontWeight: 500 }}>
          {p.value || "—"}
        </Typography>
      ),
    },
    { field: "email", headerName: "Email", flex: 1, minWidth: 140 },
    { field: "phone", headerName: "Phone", width: 130 },
    {
      field: "lastTerminalAccess",
      headerName: "Last Terminal Access",
      width: 160,
      align: "center",
      headerAlign: "center",
      renderCell: (p) => (
        <Typography variant="body2" sx={{ color: p.value ? "text.primary" : "error.main" }}>
          {p.value ? fmtDateAU(p.value) : "Never"}
        </Typography>
      ),
    },
    {
      field: "actions",
      headerName: "Open",
      width: 86,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (p) => (
        <Button
          size="small"
          variant="contained"
          onClick={() => p.row.ChemistUUID && navigate(`/admin-functions/chemist/${encodeURIComponent(p.row.ChemistUUID)}`)}
          disabled={!p.row.ChemistUUID}
          sx={{ textTransform: "none", fontWeight: 600, backgroundColor: BLUE, minWidth: 60 }}
        >
          Open
        </Button>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-white p-4 min-w-0 max-w-full">
      <Box sx={{ mb: 1.5 }}>
        <Button
          size="small"
          startIcon={<ArrowBackIos fontSize="small" />}
          onClick={() => navigate("/admin")}
          sx={{ textTransform: "none", color: "text.secondary" }}
        >
          Back
        </Button>
      </Box>

      <Typography variant="h5" sx={{ fontWeight: 800, color: BLUE, mb: 2.5 }}>
        Active subscribers with no terminal check-in
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Pharmacies with an active subscription but no terminal check-in in the last 7 days.
      </Typography>

      {loading && !data && (
        <Box className="flex items-center justify-center min-h-[320px]">
          <Typography color="textSecondary">Loading…</Typography>
        </Box>
      )}

      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

      {data && (
        <>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}>
            <TextField
              size="small"
              label="Filter by pharmacy name"
              placeholder="Search"
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 240 }}
            />
            <Typography variant="body2" color="textSecondary">
              {filteredRows.length} pharmacies
            </Typography>
          </Box>

          <Box sx={{ height: 520 }}>
            <DataGrid
              rows={filteredRows}
              columns={columns}
              loading={loading}
              density="compact"
              disableColumnMenu
              disableRowSelectionOnClick
              pageSizeOptions={[25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              sx={pharmacyHaloDataGridSx}
            />
          </Box>
        </>
      )}
    </div>
  );
};

export default AdminInactiveTerminalsPage;
